// src/encounter/centre-panel.tsx
//
// PROD-2743 · frame v2 (DARK). A panel that opens IN the centre column. Layout law (desk R5, ruled):
// no panel, drawer or viewer ever covers or replaces the Scrips Intelligence column or the chart —
// panels open in the centre. The frame hosts this in EncounterShell's centre-swap slot, so the chart
// column and the right column never move while it is open.
//
// Anatomy: a header (title, the close control named after what it closes, Esc) over the panel's own
// scroll region. Closing hands focus back to the control that opened it.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

export interface CentrePanelProps {
  /** The panel title; also its accessible name. */
  title: string;
  /** The close control's accessible name, e.g. "Close vitals history". */
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}

export function CentrePanel({ title, closeLabel, onClose, children }: CentrePanelProps) {
  const rootRef = useRef<HTMLElement>(null);
  // Focus moves INTO the panel when it opens, so Esc and the keyboard reach it at once — and back to
  // the control that opened it when it closes (Codex P2). That control is unmounted in the SAME
  // commit that mounts this panel (the panel replaces the centre), so it is identified during the
  // first render — before that commit removes it — and found again by its accessible name (or its
  // text) once the centre has re-rendered, rather than held by a stale reference.
  const [opener] = useState(() => {
    const el = typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null);
    if (!el || el === document.body) return { label: null, text: undefined };
    return { label: el.getAttribute('aria-label'), text: el.textContent?.trim() || undefined };
  });
  useEffect(() => {
    const { label, text } = opener;
    rootRef.current?.focus();
    return () => {
      if (!label && !text) return;
      setTimeout(() => {
        const buttons = Array.from(document.querySelectorAll<HTMLElement>('button'));
        const target =
          (label && buttons.find((b) => b.getAttribute('aria-label') === label)) ||
          (text && buttons.find((b) => b.textContent?.trim() === text)) ||
          null;
        target?.focus();
      }, 0);
    };
  }, [opener]);
  return (
    <section
      ref={rootRef}
      aria-label={title}
      data-slot="centre-panel"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
      className="flex-1 min-h-0 flex flex-col bg-surface-card outline-none"
    >
      <header className="shrink-0 flex flex-row items-center justify-between gap-3 px-6 py-3 border-b border-b-border-default">
        <h2 className="m-0 text-size-body-15 font-semibold text-text-primary">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="icon-action cursor-pointer"
        >
          <X className="size-4" aria-hidden />
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">{children}</div>
    </section>
  );
}
